import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  composeQueryParams,
  initialDashboardPageMetadata,
  mergeDshbPageMetadataObjects,
  paramsSerializer,
  apiError
} from "../../utils";

export const selectTopicsAsOptions = state => {
  if (state.topic.topics)
    return state.topic.topics.map(topic => ({ id: topic.id, label: topic.name }));
  else return [];
};

export const readAllTopicsForDashboardAsync = createAsyncThunk(
  "topic/readAll",
  async (payload = {}, { getState }) => {
    const { metadata } = getState().topic;
    const params = composeQueryParams(payload, metadata);
    const response = await axios.get("/api/admin/topics", { params, paramsSerializer });
    return { topics: response.data };
  }
);

export const upsertTopicAsync = createAsyncThunk(
  "topic/upsert",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/topic`, payload.topic);
      return { topic: response.data };
    } catch (error) {
      return rejectWithValue(apiError(error));
    }
  }
);

export const deleteTopicAsync = createAsyncThunk("/topic/delete", async payload => {
  await axios.delete(`/api/topic/${payload.topic.id}`);
  return { id: payload.topic.id };
});

export const topicSlice = createSlice({
  name: "topic",
  initialState: {
    loadFailed: false,
    failedLoadArgs: {},
    loading: true,
    error: null,
    topics: [],
    metadata: initialDashboardPageMetadata
  },
  reducers: {
    clearError: (state, action) => {
      if (state.error) state.error[action.payload.field] = null;
    },
    clearErrors: state => {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(readAllTopicsForDashboardAsync.pending, state => {
        state.loading = true;
        state.loadFailed = false;
      })
      .addCase(readAllTopicsForDashboardAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { items, ...metadata } = action.payload.topics || {};
        state.topics = items || [];
        state.metadata = mergeDshbPageMetadataObjects(state.metadata, metadata);
      })
      .addCase(readAllTopicsForDashboardAsync.rejected, (state, action) => {
        state.loading = false;
        if (action.meta.aborted) return;
        state.loadFailed = true;
        state.failedLoadArgs = action.meta.arg || {};
        state.topics = [];
      })
      .addCase(upsertTopicAsync.pending, state => {
        state.loading = true;
      })
      .addCase(upsertTopicAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.topics.unshift(action.payload.topic);
      })
      .addCase(upsertTopicAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.fields ?? null;
      })
      .addCase(deleteTopicAsync.pending, state => {
        state.loading = true;
      })
      .addCase(deleteTopicAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.topics = state.topics.filter(topic => topic.id !== action.payload.id);
      })
      .addCase(deleteTopicAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  }
});
export const { clearError, clearErrors } = topicSlice.actions;
export default topicSlice.reducer;
